import { useEffect, useState } from "react";
import styled from "styled-components";
import { Bio } from "../../data/constants";
import { FiDownload, FiMenu, FiX } from "react-icons/fi";
import { FaGithub, FaLinkedin, FaDev } from "react-icons/fa";

/* ============================================================
   NAV — Divine Futurism: obsidian void + celestial gold,
   a stained-glass cathedral panel that ignites on scroll.
============================================================ */

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: ${({ $scrolled, theme }) =>
    $scrolled ? theme.bgPanel || "rgba(11,10,24,0.6)" : "transparent"};
  backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? "blur(18px) saturate(140%)" : "none"};
  -webkit-backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? "blur(18px) saturate(140%)" : "none"};
  border-bottom: 1px solid
    ${({ $scrolled, theme }) => ($scrolled ? theme.border : "transparent")};
  box-shadow: ${({ $scrolled, theme }) =>
    $scrolled ? `0 1px 0 ${theme.border}, 0 18px 50px -30px ${theme.violetGlow}` : "none"};
  transition: background 0.4s ease, backdrop-filter 0.4s ease,
    border-color 0.4s ease, box-shadow 0.4s ease;
`;

const Row = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media ${({ theme }) => theme.bp.md} {
    padding: 13px 20px;
  }
`;

const Logo = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 13px;
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 600;
  font-size: 22px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.white};
  text-shadow: 0 0 24px ${({ theme }) => theme.goldGlow};

  .mark {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: linear-gradient(
      150deg,
      ${({ theme }) => theme.goldSoft},
      ${({ theme }) => theme.gold}
    );
    color: ${({ theme }) => theme.bgDeep};
    display: grid;
    place-items: center;
    font-family: ${({ theme }) => theme.fonts.serif};
    font-size: 17px;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.borderHi},
      0 0 22px ${({ theme }) => theme.goldGlow};
  }
`;

const Links = styled.nav`
  display: flex;
  align-items: center;
  gap: 34px;

  a {
    position: relative;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: ${({ theme }) => theme.textMuted};
    transition: color 0.25s ease, text-shadow 0.25s ease;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 100%;
      bottom: -7px;
      height: 1px;
      background: linear-gradient(
        90deg,
        ${({ theme }) => theme.gold},
        transparent
      );
      transition: right 0.3s ease;
    }

    &:hover {
      color: ${({ theme }) => theme.gold};
      text-shadow: 0 0 14px ${({ theme }) => theme.goldGlow};
      &::after {
        right: 0;
      }
    }
  }
  @media ${({ theme }) => theme.bp.md} {
    display: none;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .icon {
    display: inline-flex;
    color: ${({ theme }) => theme.textMuted};
    font-size: 19px;
    padding: 7px;
    transition: color 0.25s ease, text-shadow 0.25s ease, transform 0.25s ease;
    &:hover {
      color: ${({ theme }) => theme.gold};
      text-shadow: 0 0 16px ${({ theme }) => theme.goldGlow};
      transform: translateY(-1px);
    }
  }

  /* on mobile, top-bar icons + résumé move into the dropdown menu */
  @media ${({ theme }) => theme.bp.md} {
    .icon,
    .resume {
      display: none;
    }
  }

  .resume {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: ${({ theme }) => theme.bgDeep};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.goldSoft},
      ${({ theme }) => theme.gold}
    );
    padding: 10px 18px;
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.borderHi};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.border},
      0 6px 24px -10px ${({ theme }) => theme.goldGlow};
    transition: box-shadow 0.3s ease, transform 0.25s ease, filter 0.3s ease;
    &:hover {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow: 0 0 0 1px ${({ theme }) => theme.borderHi},
        0 0 28px ${({ theme }) => theme.goldGlow};
    }
  }
  @media ${({ theme }) => theme.bp.sm} {
    .resume span {
      display: none;
    }
  }
`;

const Burger = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ theme }) => theme.goldSoft};
  cursor: pointer;
  padding: 4px;
  line-height: 0;
  transition: color 0.25s ease, text-shadow 0.25s ease;
  &:hover {
    color: ${({ theme }) => theme.gold};
    text-shadow: 0 0 16px ${({ theme }) => theme.goldGlow};
  }
  @media ${({ theme }) => theme.bp.md} {
    display: inline-flex;
  }
`;

const Mobile = styled.div`
  display: none;
  @media ${({ theme }) => theme.bp.md} {
    display: ${({ $open }) => ($open ? "flex" : "none")};
    flex-direction: column;
    background: ${({ theme }) => theme.bgPanel || "rgba(11,10,24,0.85)"};
    backdrop-filter: blur(18px) saturate(140%);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
    border-top: 1px solid ${({ theme }) => theme.border};
    border-bottom: 1px solid ${({ theme }) => theme.border};
    box-shadow: 0 24px 50px -28px ${({ theme }) => theme.violetGlow};
    padding: 10px 22px 20px;
    gap: 2px;

    .navlink {
      padding: 14px 4px;
      font-family: ${({ theme }) => theme.fonts.mono};
      font-size: 13px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.16em;
      color: ${({ theme }) => theme.textMuted};
      border-bottom: 1px solid ${({ theme }) => theme.border};
      transition: color 0.25s ease, text-shadow 0.25s ease;
      &:hover {
        color: ${({ theme }) => theme.gold};
        text-shadow: 0 0 14px ${({ theme }) => theme.goldGlow};
      }
    }

    /* résumé CTA inside the menu */
    .m-resume {
      margin-top: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      padding: 13px 18px;
      border-radius: 11px;
      font-family: ${({ theme }) => theme.fonts.mono};
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      color: ${({ theme }) => theme.bgDeep};
      background: linear-gradient(
        135deg,
        ${({ theme }) => theme.goldSoft},
        ${({ theme }) => theme.gold}
      );
      box-shadow: 0 0 24px -8px ${({ theme }) => theme.goldGlow};
    }

    /* social row inside the menu */
    .m-socials {
      display: flex;
      gap: 12px;
      margin-top: 16px;

      a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 46px;
        height: 46px;
        border-radius: 12px;
        font-size: 19px;
        color: ${({ theme }) => theme.textMuted};
        background: ${({ theme }) => theme.glass};
        border: 1px solid ${({ theme }) => theme.border};
        transition: color 0.25s ease, border-color 0.25s ease;
        &:hover {
          color: ${({ theme }) => theme.gold};
          border-color: ${({ theme }) => theme.borderHi};
        }
      }
    }
  }
`;

const NAV = [
  { id: "about", label: "About" },
  { id: "skills", label: "Skills" },
  { id: "experience", label: "Experience" },
  { id: "projects", label: "Projects" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <Bar $scrolled={scrolled}>
      <Row>
        <Logo href="#home">
          <span className="mark">{Bio.initials}</span>
          {Bio.name.split(" ")[0]}
        </Logo>

        <Links>
          {NAV.map((n) => (
            <a key={n.id} href={`#${n.id}`}>
              {n.label}
            </a>
          ))}
        </Links>

        <Right>
          <a
            className="icon"
            href={Bio.github}
            target="_blank"
            rel="noreferrer"
            aria-label="GitHub"
          >
            <FaGithub />
          </a>
          <a
            className="icon"
            href={Bio.linkedin}
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin />
          </a>
          <a
            className="icon"
            href={Bio.blog}
            target="_blank"
            rel="noreferrer"
            aria-label="Blog"
            title="Blog"
          >
            <FaDev />
          </a>
          <a
            className="resume"
            href={Bio.resume}
            target="_blank"
            rel="noreferrer"
          >
            <FiDownload /> <span>Résumé</span>
          </a>
          <Burger onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <FiX /> : <FiMenu />}
          </Burger>
        </Right>
      </Row>

      <Mobile $open={open}>
        {NAV.map((n) => (
          <a
            key={n.id}
            className="navlink"
            href={`#${n.id}`}
            onClick={() => setOpen(false)}
          >
            {n.label}
          </a>
        ))}

        <a
          className="m-resume"
          href={Bio.resume}
          target="_blank"
          rel="noreferrer"
          onClick={() => setOpen(false)}
        >
          <FiDownload /> Résumé
        </a>

        <div className="m-socials">
          <a href={Bio.github} target="_blank" rel="noreferrer" aria-label="GitHub">
            <FaGithub />
          </a>
          <a href={Bio.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
            <FaLinkedin />
          </a>
          <a href={Bio.blog} target="_blank" rel="noreferrer" aria-label="Blog">
            <FaDev />
          </a>
        </div>
      </Mobile>
    </Bar>
  );
}
